"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { Plus, Edit, Trash2, Eye, Search, Filter, Package, AlertCircle, Star, X, ChevronRight } from "lucide-react"
import { productsService } from "../../services/firebase"
import { db } from "../../firebase/config"
import { writeBatch, doc } from "firebase/firestore"
import type { Product } from "../../store/slices/productsSlice"
import LoadingSpinner from "../../components/common/LoadingSpinner"
import toast from "react-hot-toast"
import { commentService, type Comment } from "../../services/commentService"
import { useSelector } from "react-redux"
import * as XLSX from "xlsx"

// Helper to slugify strings for URLs
function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

// Helper to get primary category from categories array
function getPrimaryCategory(categories: string[] | string | undefined): string {
  if (Array.isArray(categories)) {
    return categories[0] || "uncategorized"
  }
  if (typeof categories === "string") {
    return categories
  }
  return "uncategorized"
}

// Helper to get all categories as array
function getCategoriesArray(categories: string[] | string | undefined): string[] {
  if (Array.isArray(categories)) {
    return categories
  }
  if (typeof categories === "string") {
    return [categories]
  }
  return []
}

const AdminProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([])
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("")
  const [sortBy, setSortBy] = useState("name")
  const [showFilters, setShowFilters] = useState(false)

  // Bulk selection state
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([])
  const allSelected = filteredProducts.length > 0 && selectedProductIds.length === filteredProducts.length

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedProductIds([])
    } else {
      setSelectedProductIds(filteredProducts.map((p) => p.id))
    }
  }
  const handleSelectProduct = (id: string) => {
    setSelectedProductIds((prev) => (prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]))
  }

  // Bulk isActive update
  const handleBulkSetActive = async (isActive: boolean) => {
    if (selectedProductIds.length === 0) {
      toast.error("No products selected.")
      return
    }
    try {
      const batch = writeBatch(db)
      selectedProductIds.forEach((id) => {
        batch.update(doc(db, "products", id), { isActive })
      })
      await batch.commit()
      toast.success(`Set ${selectedProductIds.length} product(s) as ${isActive ? "Active" : "Inactive"}`)
      setSelectedProductIds([])
      loadProducts()
    } catch (error) {
      console.error("Bulk update failed:", error)
      toast.error("Bulk update failed")
    }
  }

  // Delete all products
  const handleDeleteAllProducts = async () => {
    if (products.length === 0) {
      toast.error("No products to delete.")
      return
    }

    const confirmMessage = `Are you sure you want to delete ALL ${products.length} products? This action cannot be undone!`
    if (!window.confirm(confirmMessage)) {
      return
    }

    try {
      setIsLoading(true)
      const batch = writeBatch(db)

      // Delete all products
      products.forEach((product) => {
        batch.delete(doc(db, "products", product.id))
      })

      await batch.commit()
      toast.success(`Successfully deleted all ${products.length} products`)
      setSelectedProductIds([])
      loadProducts()
    } catch (error) {
      console.error("Delete all products failed:", error)
      toast.error("Failed to delete all products")
    } finally {
      setIsLoading(false)
    }
  }

  const categories = ["beds", "sofas", "dining", "wardrobes", "chairs", "tables", "modular", "storage"]

  const { items: wishlistItems = [] } = useSelector((state: { wishlist: { items: Product[] } }) => state.wishlist)

  useEffect(() => {
    loadProducts()
  }, [])

  useEffect(() => {
    filterProducts()
  }, [products, searchQuery, selectedCategory, sortBy])

  const loadProducts = async () => {
    try {
      setIsLoading(true)
      const productsData = await productsService.getProducts()
      setProducts(productsData)
    } catch (error) {
      console.error("Error loading products:", error)
      toast.error("Failed to load products")
    } finally {
      setIsLoading(false)
    }
  }

  const filterProducts = () => {
    let filtered = [...products]

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter((product) => {
        const productCategories = getCategoriesArray((product as any).categories || (product as any).category)
        return (
          product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          productCategories.some((cat) => cat.toLowerCase().includes(searchQuery.toLowerCase())) ||
          product.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
        )
      })
    }

    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter((product) => {
        const productCategories = getCategoriesArray((product as any).categories || (product as any).category)
        return productCategories.includes(selectedCategory)
      })
    }

    // Sort
    switch (sortBy) {
      case "name":
        filtered.sort((a, b) => a.name.localeCompare(b.name))
        break
      case "price-low":
        filtered.sort((a, b) => a.price - b.price)
        break
      case "price-high":
        filtered.sort((a, b) => b.price - a.price)
        break
      case "stock":
        filtered.sort((a, b) => b.stock - a.stock)
        break
      case "rating":
        filtered.sort((a, b) => b.rating - a.rating)
        break
      case "newest":
        filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        break
      default:
        break
    }

    setFilteredProducts(filtered)
  }

  const handleDeleteProduct = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await productsService.deleteProduct(id)
        toast.success("Product deleted successfully")
        loadProducts()
      } catch (error) {
        console.error("Error deleting product:", error)
        toast.error("Failed to delete product")
      }
    }
  }

  const getStockStatus = (stock: number) => {
    if (stock === 0) return { text: "Out of Stock", color: "text-red-600 bg-red-100" }
    if (stock < 5) return { text: "Low Stock", color: "text-orange-600 bg-orange-100" }
    return { text: "In Stock", color: "text-green-600 bg-green-100" }
  }

  // Real wishlist count from Redux (if available)
  const getWishlistCount = (productId: string) => {
    return wishlistItems.filter((item: Product) => item.id === productId).length
  }

  // Compute engagement as reviews + commentCount + wishlistCount
  const getEngagement = (product: Product) => {
    const wishlistCount = getWishlistCount(product.id)
    return (product.reviews || 0) + (product.commentCount || 0) + wishlistCount
  }

  // Modal state for comments
  const [showCommentsModal, setShowCommentsModal] = useState(false)
  const [modalComments, setModalComments] = useState<Comment[]>([])
  const [modalProduct, setModalProduct] = useState<Product | null>(null)

  const openCommentsModal = async (product: Product) => {
    setModalProduct(product)
    setShowCommentsModal(true)
    const comments = await commentService.getComments(product.id)
    setModalComments(comments)
  }
  const closeCommentsModal = () => {
    setShowCommentsModal(false)
    setModalComments([])
    setModalProduct(null)
  }

  // Most engaging, most sold, highest rated products
  const mostEngagedProduct = products.reduce(
    (max, p) => (getEngagement(p) > getEngagement(max) ? p : max),
    products[0] || { reviews: 0, commentCount: 0, id: "", name: "", categories: [], price: 0 },
  )
  const mostSoldProduct = products.reduce(
    (max, p) => (p.reviews > max.reviews ? p : max),
    products[0] || { reviews: 0, id: "", name: "", categories: [], price: 0 },
  )
  const highestRatedProduct = products.reduce(
    (max, p) => (p.rating > max.rating ? p : max),
    products[0] || { rating: 0, id: "", name: "", categories: [], price: 0 },
  )

  // Export handler for products table
  const exportProducts = (type: "csv" | "excel" = "csv") => {
    const headers = [
      "Product ID",
      "Name",
      "Categories",
      "Price",
      "Stock",
      "Rating",
      "Reviews",
      "Engagement",
      "Created At",
      "Description",
    ]
    const rows = filteredProducts.map((product) => [
      product.id,
      product.name,
      getCategoriesArray((product as any).categories || (product as any).category).join("; "),
      product.price,
      product.stock,
      product.rating,
      product.reviews,
      getEngagement(product),
      product.createdAt || "",
      product.description || "",
    ])
    if (type === "excel") {
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
      XLSX.utils.book_append_sheet(wb, ws, "Products")
      XLSX.writeFile(wb, `products-${new Date().toISOString().split("T")[0]}.xlsx`)
      return
    }
    let csvContent = ""
    csvContent += headers.join(",") + "\n"
    csvContent += rows.map((row) => row.map((field) => `"${field}"`).join(",")).join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `products-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }
  const [showExportDropdown, setShowExportDropdown] = useState(false)

  const topScrollRef = useRef<HTMLDivElement>(null)
  const tableScrollRef = useRef<HTMLDivElement>(null)

  const handleTopScroll = () => {
    if (topScrollRef.current && tableScrollRef.current) {
      tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft
    }
  }

  const handleTableScroll = () => {
    if (topScrollRef.current && tableScrollRef.current) {
      topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft
    }
  }

  const scrollToRight = () => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollTo({
        left: tableScrollRef.current.scrollWidth - tableScrollRef.current.clientWidth,
        behavior: "smooth",
      })
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products Management</h1>
          <p className="text-gray-600">Manage your furniture inventory</p>
        </div>
        <Link
          to="/admin/products/add"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
        >
          <Plus className="w-5 h-5 mr-2" /> Add Product
        </Link>
      </div>
      {/* Bulk Actions */}
      <div className="flex items-center gap-4 mb-2">
        <button
          className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          onClick={() => handleBulkSetActive(true)}
          disabled={selectedProductIds.length === 0}
        >
          Set Active
        </button>
        <button
          className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          onClick={() => handleBulkSetActive(false)}
          disabled={selectedProductIds.length === 0}
        >
          Set Inactive
        </button>
        <span className="text-sm text-gray-500">{selectedProductIds.length} selected</span>

        {/* Delete All Products Button */}
        <div className="ml-auto">
          <button
            className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
            onClick={handleDeleteAllProducts}
            disabled={products.length === 0 || isLoading}
          >
            <Trash2 className="w-4 h-4" />
            Delete All Products ({products.length})
          </button>
        </div>
      </div>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Package className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Products</p>
              <p className="text-2xl font-semibold text-gray-900">{products.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <AlertCircle className="w-8 h-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Low Stock</p>
              <p className="text-2xl font-semibold text-gray-900">
                {products.filter((p) => p.stock < 5 && p.stock > 0).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Package className="w-8 h-8 text-red-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Out of Stock</p>
              <p className="text-2xl font-semibold text-gray-900">{products.filter((p) => p.stock === 0).length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Package className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Featured</p>
              <p className="text-2xl font-semibold text-gray-900">{products.filter((p) => p.isFeatured).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Star className="w-8 h-8 text-yellow-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Most Engaged Product</p>
              <p className="text-base font-semibold text-gray-900">{mostEngagedProduct?.name || "-"}</p>
              <p className="text-xs text-gray-500">Engagement: {getEngagement(mostEngagedProduct)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Package className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Most Sold Product</p>
              <p className="text-base font-semibold text-gray-900">{mostSoldProduct?.name || "-"}</p>
              <p className="text-xs text-gray-500">Sales: {mostSoldProduct?.reviews || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Star className="w-8 h-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Highest Rated Product</p>
              <p className="text-base font-semibold text-gray-900">{highestRatedProduct?.name || "-"}</p>
              <p className="text-xs text-gray-500">Rating: {highestRatedProduct?.rating?.toFixed(1) || "-"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex items-center space-x-4">
            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="name">Sort by Name</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="stock">Stock Level</option>
              <option value="rating">Rating</option>
              <option value="newest">Newest First</option>
            </select>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
            <div className="relative">
              <button
                className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                onClick={() => setShowExportDropdown((v) => !v)}
                type="button"
              >
                <Package className="w-4 h-4" />
                <span>Export</span>
              </button>
              {showExportDropdown && (
                <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      exportProducts("csv")
                      setShowExportDropdown(false)
                    }}
                  >
                    Export as CSV
                  </button>
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      exportProducts("excel")
                      setShowExportDropdown(false)
                    }}
                  >
                    Export as Excel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Results Count */}
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredProducts.length} of {products.length} products
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="flex items-center border-b border-gray-200 bg-gray-50">
          <div
            ref={topScrollRef}
            className="flex-1 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200"
            onScroll={handleTopScroll}
            style={{ height: "20px" }}
          >
            <div style={{ width: "100%", minWidth: "1200px", height: "1px" }}></div>
          </div>
          <button
            onClick={scrollToRight}
            className="flex-shrink-0 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors border-l border-gray-200"
            title="Scroll to view actions"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div
          ref={tableScrollRef}
          className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          onScroll={handleTableScroll}
        >
          <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: "1200px" }}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    aria-label="Select all products"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categories
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Wishlist
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Engagement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProducts.map((product) => {
                const stockStatus = getStockStatus(product.stock)
                const productCategories = getCategoriesArray((product as any).categories || (product as any).category)
                const primaryCategory = getPrimaryCategory((product as any).categories || (product as any).category)

                return (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedProductIds.includes(product.id)}
                        onChange={() => handleSelectProduct(product.id)}
                        aria-label={`Select product ${product.name}`}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <img
                          src={
                            Array.isArray(product.images) && product.images[0] ? product.images[0] : "/placeholder.svg"
                          }
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                          <div className="text-sm text-gray-500">ID: {product.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {productCategories.map((category, index) => (
                          <span
                            key={index}
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                              index === 0 ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {category}
                          </span>
                        ))}
                        {productCategories.length === 0 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            uncategorized
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">₹{product.price.toLocaleString()}</div>
                      {product.originalPrice && (
                        <div className="text-sm text-gray-500 line-through">
                          ₹{product.originalPrice.toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}
                      >
                        {product.stock} units
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{getWishlistCount(product.id)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{getEngagement(product)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        {product.isFeatured && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Featured
                          </span>
                        )}
                        {product.isNewArrival && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            New
                          </span>
                        )}
                        {product.isBestSeller && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            Best Seller
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-3">
                        <Link
                          to={`/${slugify(primaryCategory)}/${slugify(product.name)}`}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                          title="View Product"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <Link
                          to={`/admin/products/edit/${product.id}`}
                          className="text-primary-600 hover:text-primary-700 p-1 rounded hover:bg-primary-50"
                          title="Edit Product"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDeleteProduct(product.id, product.name)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                          title="Delete Product"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openCommentsModal(product)}
                          className="text-yellow-600 hover:text-yellow-800 p-1 rounded hover:bg-yellow-50"
                          title="View Comments"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>

      {/* Comments Modal */}
      {showCommentsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6 relative">
            <button className="absolute top-2 right-2 text-gray-500 hover:text-red-600" onClick={closeCommentsModal}>
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold mb-4">Comments for {modalProduct?.name}</h2>
            {modalComments.length === 0 ? (
              <p className="text-gray-500">No comments for this product.</p>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {modalComments.map((comment) => (
                  <div key={comment.id} className="border-b pb-2">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900">{comment.userName}</span>
                      <span className="text-xs text-gray-500">{new Date(comment.createdAt).toLocaleString()}</span>
                    </div>
                    <p className="text-gray-700 mt-1 mb-1">{comment.content}</p>
                    {comment.likesCount > 0 && (
                      <div className="flex items-center text-blue-500 text-xs">
                        <span className="mr-1">❤️</span>
                        {comment.likesCount} likes
                      </div>
                    )}
                    {comment.replies && comment.replies.length > 0 && (
                      <div className="ml-4 mt-2 border-l-2 border-gray-200 pl-2">
                        <span className="text-xs text-gray-500">Replies:</span>
                        {comment.replies.map((reply) => (
                          <div key={reply.id} className="mt-1">
                            <span className="font-semibold text-gray-800">{reply.userName}:</span> {reply.content}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminProducts
